import { t } from 'i18next'

import type {
  SpendLineChartProps,
  SpendBarChartProps
} from '../components/BudgetConsumptionReport'
import type {
  TotalSpendResponse,
  DailySpendResponse,
  HourlySpendResponse,
  CumulativeDailyResponse
} from '../services/campaign/spend-reports'

// ---------------------------------------------------------------------------
// Error helpers
// ---------------------------------------------------------------------------

export const isNoSpendDataError = (error: unknown): boolean => {
  if (!error || typeof error !== 'object') return false
  const e = error as {
    response?: { status?: number; data?: { messages?: string[] } }
  }
  return (
    e.response?.status === 422 &&
    e.response?.data?.messages?.[0] === 'Campaign has no recorded spend data'
  )
}

export const isNotFoundError = (error: unknown): boolean => {
  if (!error || typeof error !== 'object') return false
  const e = error as { response?: { status?: number } }
  return e.response?.status === 404
}

// ---------------------------------------------------------------------------
// Retry
// ---------------------------------------------------------------------------

export const fibonacci = (n: number): number => {
  if (n <= 1) return n
  let a = 0,
    b = 1
  for (let i = 2; i <= n; i++) [a, b] = [b, a + b]
  return b
}

// attempt 0 → 1s | 1 → 1s | 2 → 2s | 3 → 3s | 4 → 5s | 5 → 8s (cap: 30s)
export const fibonacciRetryDelay = (attempt: number): number =>
  Math.min(fibonacci(attempt + 1) * 1000, 30_000)

// ---------------------------------------------------------------------------
// Date helpers
// ---------------------------------------------------------------------------

export const formatDate = (iso: string): string => {
  const [, month, day] = iso.split('-')
  const monthKey = `rm:spendReport.chart.monthAbbr.${Number(month) - 1}`
  return `${day} ${t(monthKey)}`
}

export const todayIso = (): string => new Date().toISOString().split('T')[0]

// ---------------------------------------------------------------------------
// Mappers (snake_case API → camelCase chart props)
// ---------------------------------------------------------------------------

type SpendLineDataPoint = {
  cumulative: number | null
  forecast: number | null
  goal: number
  forecast_upper_bound: number | null
  forecast_lower_bound: number | null
}

type SpendBarDataPoint = {
  consumed: number | null
  overspend: number | null
}

const centsToCurrency = (value: number | null): number | null =>
  value === null ? null : value / 100

const getConsumedWithoutOverspend = (
  consumed: number | null,
  overspend: number | null
): number | null =>
  consumed === null ? null : Math.max(0, consumed - (overspend ?? 0))

const mapSpendLineSeries = (data: SpendLineDataPoint[]) => ({
  cumulativeData: data.map(p => centsToCurrency(p.cumulative)),
  forecastData: data.map(p => centsToCurrency(p.forecast)),
  goalData: data.map(p => p.goal / 100),
  bandHighData: data.map(p => centsToCurrency(p.forecast_upper_bound)),
  bandLowData: data.map(p => centsToCurrency(p.forecast_lower_bound))
})

const mapSpendBarSeries = (data: SpendBarDataPoint[], goal: number) => ({
  consumedData: data.map(p =>
    centsToCurrency(getConsumedWithoutOverspend(p.consumed, p.overspend))
  ),
  remainingData: data.map(p =>
    centsToCurrency(
      Math.max(
        0,
        goal - (getConsumedWithoutOverspend(p.consumed, p.overspend) ?? 0)
      )
    )
  ),
  aboveGoalData: data.map(p => centsToCurrency(p.overspend))
})

export const mapTotalSpend = (
  d: TotalSpendResponse,
  campaignEndAt?: string
): SpendLineChartProps => {
  const today = todayIso()
  const todayInData = d.data.some(p => p.date === today)
  const endIndex =
    campaignEndAt == null ? -1 : d.data.findIndex(p => p.date === campaignEndAt)
  return {
    labels: d.data.map(p => formatDate(p.date)),
    ...mapSpendLineSeries(d.data),
    elapsedIndex: todayInData ? d.elapsed_index : -1,
    epoch: new Date(d.reference_timestamp).getTime(),
    pillLabel: t('rm:spendReport.chart.today'),
    currency: d.currency,
    endIndex: endIndex >= 0 ? endIndex : undefined
  }
}

export const mapDailySpend = (
  d: DailySpendResponse,
  campaignEndAt?: string
): SpendBarChartProps => {
  const today = todayIso()
  const todayInData = d.data.some(p => p.date === today)
  const endIndex =
    campaignEndAt == null ? -1 : d.data.findIndex(p => p.date === campaignEndAt)
  return {
    labels: d.data.map(p => formatDate(p.date)),
    ...mapSpendBarSeries(d.data, d.daily_budget),
    impressionShareData: d.data.map(p => p.impression_share ?? null),
    lostIsRankData: d.data.map(p => p.lost_is_rank ?? null),
    elapsedIndex: todayInData ? d.elapsed_index : -1,
    pillLabel: t('rm:spendReport.chart.today'),
    goalValue: d.daily_budget / 100,
    currency: d.currency,
    endIndex: endIndex >= 0 ? endIndex : undefined
  }
}

export const mapHourlySpend = (
  d: HourlySpendResponse,
  date: string
): SpendBarChartProps => ({
  labels: d.data.map(p => `${p.hour}h`),
  ...mapSpendBarSeries(d.data, d.hourly_goal),
  elapsedIndex: date === todayIso() ? d.elapsed_index : -1,
  pillLabel: t('rm:spendReport.chart.now'),
  goalValue: d.hourly_goal / 100,
  hourly: true,
  currency: d.currency
})

export const mapCumulativeDaily = (
  d: CumulativeDailyResponse,
  date: string
): SpendLineChartProps => ({
  labels: d.data.map(p => `${p.hour}h`),
  ...mapSpendLineSeries(d.data),
  elapsedIndex: date === todayIso() ? d.elapsed_index : -1,
  pillLabel: t('rm:spendReport.chart.now'),
  hourly: true,
  currency: d.currency
})
